import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, Check, Download, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { CopyButton } from "@/components/atoms/CopyButton";
import { IconButton } from "@/components/atoms/IconButton";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { MultiSelect } from "@/components/molecules/MultiSelect";
import { BranchScopeSelect } from "@/components/molecules/ScopePicker";
import { Drawer } from "@/components/organisms/Drawer";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import { DiaryDateTimeField } from "@/modules/diary/DiaryDateTimeField";
import { campaignsService, getCampaignImportRowErrors } from "@/services/campaignsService";
import { labelsService, type LabelReachoutPreview } from "@/services/labelsService";
import { whatsappTemplateService, type WhatsappTemplate } from "@/services/whatsappTemplateService";
import { propertiesService } from "@/services/propertiesService";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import type { PropertySummary } from "@/types/entities";
import { useSettingsStore } from "@/store/settingsStore";
import { useProfileStore } from "@/store/profileStore";
import { cn } from "@/lib/cn";
import { getApiErrorMessage } from "@/utils/apiErrors";
// The same full IANA list the Settings timezone field uses, so a campaign can
// be scheduled in any zone the rest of the product offers.
import { TIMEZONE_OPTIONS } from "@/types/availability";
import type {
  Campaign,
  CampaignContactScanResult,
  CampaignCsv,
  CampaignImportRowError,
  CampaignLabel,
  CampaignWriteBody,
} from "@/types/campaign";
import { CampaignImportErrorsModal } from "./CampaignImportErrorsModal";
import { CampaignScopeChangeModal } from "./CampaignScopeChangeModal";
import { CampaignContactScanModal } from "./CampaignContactScanModal";
import { CampaignReachoutPreviewModal } from "./CampaignReachoutPreviewModal";
import { readScanResponse, toContactScanRows } from "./campaignContactScan";
import {
  DEFAULT_TIMEZONE,
  SEND_MODE_OPTIONS,
  TARGET_TYPE_FORM_OPTIONS,
  datetimeLocalToWallClock,
  isoToDatetimeLocal,
  zonedWallClockToUtcIso,
} from "./campaignConstants";
import { RecurringScheduleFields } from "./RecurringScheduleFields";
import { cronFromCadencePreset } from "./campaignCron";
import {
  MAX_CSV_ROWS,
  SAMPLE_CSV_FILENAME,
  SAMPLE_CSV_URL,
  parseCampaignCsv,
  type CsvParseResult,
} from "./campaignCsv";

/**
 * Whether two instants are the same moment. Compared by value, not by string —
 * the service returns `2026-08-11T21:05:00Z` where we would build
 * `2026-08-11T21:05:00.000Z`, and those must not read as an edit.
 */
function sameInstant(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const [ta, tb] = [new Date(a).getTime(), new Date(b).getTime()];
  if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
  return ta === tb;
}

interface Props {
  open: boolean;
  /** null → create mode; a campaign → edit mode. */
  campaign: Campaign | null;
  onClose: () => void;
  onSaved: (campaign: Campaign) => void;
}

/** One page of the property dropdown — the same size Reach Out pages its list at. */
const PROPERTY_PAGE_SIZE = 50;

interface FormState {
  name: string;
  targetType: string;
  sendMode: string;
  timezone: string;
  templateId: string;
  /**
   * Property this campaign is about, sent as `property_id`. Only offered once a template
   * is chosen — the property fills that template's placeholders, so on its own it has
   * nothing to say.
   */
  propertyId: string;
  labelIds: string[];
  /**
   * Hand-entered id + name pairs, used when the labels endpoint is
   * unavailable. The service rejects a blank label name, so the id alone
   * is not enough to build a valid request.
   */
  manualLabels: { id: string; name: string }[];
  scheduledAt: string;
  cadence: string;
  cadenceCron: string;
  repeatIndefinite: boolean;
  endAt: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  targetType: "WHATSAPP",
  sendMode: "SCHEDULED_ONCE",
  timezone: DEFAULT_TIMEZONE,
  templateId: "",
  propertyId: "",
  labelIds: [],
  manualLabels: [{ id: "", name: "" }],
  scheduledAt: "",
  cadence: "CUSTOM",
  cadenceCron: cronFromCadencePreset("DAILY"),
  repeatIndefinite: true,
  endAt: "",
};

function toForm(campaign: Campaign): FormState {
  const campaignLabels = campaign.labels ?? [];
  const labelIds = campaignLabels.map((l) => String(l.id));
  const timezone = campaign.timezone ?? DEFAULT_TIMEZONE;
  return {
    name: campaign.name ?? "",
    targetType: String(campaign.target_type ?? "WHATSAPP"),
    sendMode: String(campaign.send_mode ?? "SCHEDULED_ONCE"),
    timezone,
    templateId: campaign.template_id != null ? String(campaign.template_id) : "",
    propertyId: campaign.property_id != null ? String(campaign.property_id) : "",
    labelIds,
    manualLabels:
      campaignLabels.length > 0
        ? campaignLabels.map((l) => ({ id: String(l.id), name: l.name ?? "" }))
        : [{ id: "", name: "" }],
    // Stored instants are UTC; the pickers work in the campaign's own zone,
    // which is the frame the service reads times back in.
    scheduledAt: isoToDatetimeLocal(campaign.scheduled_at, timezone),
    cadence: "CUSTOM",
    cadenceCron: campaign.cadence_cron?.trim() || cronFromCadencePreset(campaign.cadence),
    repeatIndefinite: campaign.end_at == null,
    endAt: isoToDatetimeLocal(campaign.end_at, timezone),
  };
}

/** Hand-entered rows that are complete enough to send. */
function usableManualLabels(rows: { id: string; name: string }[]): { id: string; name: string }[] {
  return rows
    .map((row) => ({ id: row.id.trim(), name: row.name.trim() }))
    .filter((row) => row.id.length > 0 && row.name.length > 0);
}

function FormRow({ label, required, hint, children }: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bridge-subtle">
        {label}
        {required && <span className="ml-0.5 text-bridge-danger">*</span>}
      </div>
      {children}
      {hint && <p className="mt-1 text-xs text-bridge-muted">{hint}</p>}
    </div>
  );
}

export function CampaignFormDrawer({ open, campaign, onClose, onSaved }: Props) {
  const isEdit = campaign != null;
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Row-wise failures from the last rejected CSV import — null when there are none. */
  const [importErrors, setImportErrors] = useState<{
    errors: CampaignImportRowError[];
    detail: string | null;
  } | null>(null);
  const [labels, setLabels] = useState<CampaignLabel[] | null>(null);
  const [templates, setTemplates] = useState<WhatsappTemplate[] | null>(null);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  /**
   * True from opening an edit until the first page of properties has come back — the
   * campaign already holds a property, so the field has a name to resolve before the
   * form is worth reading. A create starts with no template and so nothing pending.
   */
  const [propertiesInitialPending, setPropertiesInitialPending] = useState(false);
  const { legalEntityId, legalEntityName } = useSettingsStore();

  /**
   * Which legal entity + branch this campaign is set up under, picked in the
   * drawer the same way Reach Out picks the branch it sends from. The choice both
   * scopes what the form offers — templates, labels, properties, and the entity a
   * CSV import lands its contacts in — and travels on create as
   * `legal_entity_id`/`branch_id`, which the campaign reads back on edit. It starts
   * from the account-level selection and is fixed once the campaign exists.
   */
  const { branches: scopeBranches, labels: scopeLabels } = useScopeOptions();
  /**
   * Legal entities ticked in the picker. Empty means "All legal entities" — the
   * template/label calls then omit `legalEntityId`. A create is seeded from settings.
   */
  const [pickerEntityIds, setPickerEntityIds] = useState<string[]>([]);
  const [pickerBranchId, setPickerBranchId] = useState("");
  /**
   * A scope the user picked that is waiting on confirmation, held here because
   * accepting it wipes the template, labels and property chosen under the old one.
   */
  const [pendingScope, setPendingScope] = useState<{ entityIds: string[]; branchId: string } | null>(
    null,
  );
  /**
   * The scope the campaign itself was created under, as read back from the service.
   * In edit mode this — not the account-level header selection — is what the form's
   * lookups run against, so the template, labels and property it stores are found by
   * id and render as names rather than falling back to raw ids.
   */
  const campaignLegalEntityId = campaign?.legal_entity_id ? String(campaign.legal_entity_id) : "";
  const campaignBranchId = campaign?.branch_id ? String(campaign.branch_id) : "";

  const pickedBranch = useMemo(
    () => scopeBranches.find((b) => b.id === pickerBranchId),
    [scopeBranches, pickerBranchId],
  );

  const scopeLegalEntityId = isEdit
    ? campaignLegalEntityId
    : pickedBranch?.legalEntityId || (pickerEntityIds.length === 1 ? pickerEntityIds[0] : "");
  const allLegalEntities = !isEdit && pickerEntityIds.length === 0;
  /**
   * An edit queries the campaign's own branch, with no fall back to the header's:
   * a campaign saved entity-wide has no branch, and borrowing one here would narrow
   * its lists to a branch it was never scoped to.
   */
  const scopeBranchIdForQueries = isEdit ? campaignBranchId : pickerBranchId;
  /**
   * Recipients hang off a branch: labels are listed per branch and an uploaded CSV
   * imports its contacts into one, so neither is offered until a new campaign has a
   * branch picked. An edit is exempt — its scope is fixed, and an entity-wide
   * campaign saved before this rule must still be editable.
   */
  const branchChosen = isEdit || pickerBranchId.trim().length > 0;

  /**
   * How the campaign's scope reads in the save toasts, kept as two parts so the
   * branch can be picked out in bold: the entity, and the branch it is saved
   * under — "all branches" when the campaign is entity-wide.
   */
  const scopeDisplay = useMemo(() => {
    const entity = scopeLegalEntityId ? scopeLabels[scopeLegalEntityId] ?? "" : legalEntityName ?? "";
    const branch = scopeBranchIdForQueries ? scopeLabels[scopeBranchIdForQueries] ?? "" : "";
    return { entity, branch: branch || (entity ? "all branches" : "") };
  }, [scopeBranchIdForQueries, scopeLegalEntityId, scopeLabels, legalEntityName]);

  /**
   * Body of a successful save toast: the campaign name in bold, the scope it was
   * saved under, and an optional trailing sentence (the CSV import count).
   */
  const saveMessage = (name: string | null | undefined, verb: "created" | "updated", suffix?: string) => {
    const trimmed = (name ?? form.name).trim();
    const { entity, branch } = scopeDisplay;
    return (
      <span>
        Campaign{trimmed ? <> <strong>{trimmed}</strong></> : null} {verb}
        {branch ? (
          <>
            {" for "}
            {entity ? `${entity} — ` : ""}
            <strong>{branch}</strong>
          </>
        ) : null}
        .{suffix ? ` ${suffix}` : ""}
      </span>
    );
  };

  /**
   * Atlas user id for the contact scan's `actor_user_id` — the scan attributes the
   * comparison to whoever is uploading. Unlike the campaign service's numeric
   * `X-User-Id`, this endpoint is Atlas's own, so the profile's UUID goes as-is.
   */
  const actingUserId = useProfileStore((state) => state.profileData?.userId ?? "");

  // Recipients come either from labels or from an uploaded CSV that carries its
  // own labels column — never both, so the two are a choice rather than a pair
  // of fields. Editing keeps the label picker: the GET response has no `csv`,
  // so there is nothing to show back or diff against.
  const [recipientSource, setRecipientSource] = useState<"labels" | "csv">("labels");
  const [csvFileName, setCsvFileName] = useState("");
  const [csv, setCsv] = useState<CsvParseResult | null>(null);
  /**
   * Scanned CSV rows held while the user decides which of them to import. Null
   * means no review is open — either the scan has not run yet or it came back
   * empty.
   */
  const [scanReview, setScanReview] = useState<{
    results: CampaignContactScanResult[];
  } | null>(null);
  /**
   * Label reachout dry-run held until the user confirms create. Null means the
   * check has not run (or was dismissed).
   */
  const [reachoutPreview, setReachoutPreview] = useState<LabelReachoutPreview | null>(null);
  /**
   * CSV payload waiting on the reachout preview. Set after the scan is confirmed
   * and consumed when the campaign is actually created.
   */
  const [pendingCsv, setPendingCsv] = useState<CampaignCsv | null>(null);
  /**
   * Labels created/returned by Atlas import, used for the messaging dry-run
   * and for campaign create (CSV is not sent again).
   */
  const [importedLabels, setImportedLabels] = useState<{ id: string; name: string }[]>([]);
  const csvMode = !isEdit && recipientSource === "csv";

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key as string] ? { ...prev, [key as string]: "" } : prev));
  }, []);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setImportErrors(null);
    setForm(campaign ? toForm(campaign) : EMPTY_FORM);
    setRecipientSource("labels");
    setCsvFileName("");
    setCsv(null);
    setScanReview(null);
    setReachoutPreview(null);
    setPendingCsv(null);
    setImportedLabels([]);
    setPendingScope(null);
    // Edit restores the campaign's own entity/branch. Create seeds from settings
    // (empty entity list = All legal entities, which omits legalEntityId on fetches).
    setPickerEntityIds(
      campaign
        ? campaign.legal_entity_id
          ? [String(campaign.legal_entity_id)]
          : []
        : legalEntityId
          ? [legalEntityId]
          : [],
    );
    // A new campaign starts with no branch: the pick is mandatory and drives which
    // templates, labels and recipients are on offer, so it is left to the user rather
    // than inherited from the topbar.
    setPickerBranchId(campaign?.branch_id ? String(campaign.branch_id) : "");
    // Only an edit arrives with a template already chosen, which is what makes the
    // property list load without the user touching anything.
    setPropertiesInitialPending(String(campaign?.template_id ?? "").trim().length > 0);
  }, [open, campaign, legalEntityId]);

  async function handleCsvFile(file: File | undefined) {
    if (!file) return;
    // The picker is disabled without a branch; this covers a file dropped on it anyway.
    if (!branchChosen) return;
    setCsvFileName(file.name);
    setErrors((prev) => ({ ...prev, csv: "" }));
    setImportErrors(null);
    try {
      setCsv(parseCampaignCsv(await file.text()));
    } catch {
      setCsv({
        payload: null,
        errors: ["That file could not be read as text."],
        headers: [],
        rowCount: 0,
        mandatoryHeaders: [],
      });
    }
  }

  // Labels come from Atlas (`/api/ui/v1/labels`), the same store the New Label
  // modal writes to — not from the campaign service. Wait until a branch is
  // picked on create: the fields are locked until then, and fetching with
  // `branchId=0` on open raced the scope-seed effect and fired twice.
  useEffect(() => {
    if (!open) {
      setLabels(null);
      setLoadingLabels(false);
      return;
    }
    if (!branchChosen) {
      setLabels([]);
      setLoadingLabels(false);
      return;
    }
    let cancelled = false;
    setLoadingLabels(true);
    const queryEntityIds = isEdit
      ? campaignLegalEntityId
        ? [campaignLegalEntityId]
        : []
      : pickerEntityIds.length > 0
        ? pickerEntityIds
        : legalEntityId
          ? [legalEntityId]
          : [];
    const branchId = scopeBranchIdForQueries || "0";
    // Create: a branch pick should still include entity-wide labels (`branchId=0`).
    // One `branchIds` request, not two GETs.
    const branchIds =
      !isEdit && branchId !== "0" ? [branchId, "0"] : undefined;

    const load = async () => {
      const scopeFor = (entityId: string | undefined) =>
        branchIds
          ? {
              legalEntityId: entityId,
              branchIds,
              skipGlobalBranchId: true,
              skipGlobalContext: true,
            }
          : {
              legalEntityId: entityId,
              branchId,
              skipGlobalBranchId: true,
            };

      if (queryEntityIds.length <= 1) {
        return labelsService.listAll(scopeFor(queryEntityIds[0]));
      }
      const batches = await Promise.all(
        queryEntityIds.map((id) => labelsService.listAll(scopeFor(id))),
      );
      const seen = new Set<string>();
      return batches.flat().filter((label) => {
        const id = String(label.id ?? "");
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    };

    load()
      .then((res) => {
        if (cancelled) return;
        setLabels(res.map((label) => ({ id: label.id, name: label.name })));
      })
      .catch(() => !cancelled && setLabels(null))
      .finally(() => !cancelled && setLoadingLabels(false));
    return () => {
      cancelled = true;
    };
  }, [open, branchChosen, isEdit, campaignLegalEntityId, pickerEntityIds, scopeBranchIdForQueries, legalEntityId]);

  // Templates: GET /api/ui/v1/whatsapp/templates. Same gate as labels — no
  // request until create has a branch, so opening the drawer does not double-fetch.
  useEffect(() => {
    if (!open) {
      setTemplates(null);
      setLoadingTemplates(false);
      return;
    }
    if (!branchChosen) {
      setTemplates([]);
      setLoadingTemplates(false);
      return;
    }
    let cancelled = false;
    setLoadingTemplates(true);
    const queryEntityIds = isEdit
      ? campaignLegalEntityId
        ? [campaignLegalEntityId]
        : []
      : pickerEntityIds;
    const request =
      queryEntityIds.length > 1
        ? whatsappTemplateService.listForEntities(queryEntityIds, scopeBranchIdForQueries || undefined)
        : whatsappTemplateService.list(
            queryEntityIds[0] ?? "",
            scopeBranchIdForQueries || undefined,
          );
    request
      .then((res) => !cancelled && setTemplates(res))
      .catch(() => !cancelled && setTemplates([]))
      .finally(() => !cancelled && setLoadingTemplates(false));
    return () => {
      cancelled = true;
    };
  }, [open, branchChosen, isEdit, campaignLegalEntityId, pickerEntityIds, scopeBranchIdForQueries]);

  /**
   * Properties for the drawer's legal entity + branch, fetched the same way Reach Out
   * fetches them: server-side search and paging, because an entity holds far more
   * properties than one page and filtering locally would only ever search the first slice.
   *
   * Nothing is requested until a template is chosen — the list is there to fill that
   * template's placeholders, so an untouched Template field means an untouched call.
   */
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [propertySearch, setPropertySearch] = useState("");
  const [propertyPage, setPropertyPage] = useState(0);
  const [propertiesHasNext, setPropertiesHasNext] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  /** Ticket so a slow page can never overwrite the results of a newer search. */
  const propertyFetchSeq = useRef(0);
  /** Read inside the fetch callback, which is memoised and would otherwise close over a
   *  stale pick. */
  const propertyIdRef = useRef("");
  /**
   * Set when the template changes with a property already picked: that pick is kept until
   * the new template's list has been seen, then dropped if the list doesn't contain it.
   */
  const revalidatePropertyRef = useRef(false);

  /**
   * The chosen template, which both gates the property field and keys its list: a
   * different template means a different set of properties to offer, so switching it
   * refetches rather than leaving the previous template's list in place.
   */
  const selectedTemplateId = form.templateId.trim();
  const templateChosen = selectedTemplateId.length > 0;
  propertyIdRef.current = form.propertyId;

  const propertyScope = useMemo(() => {
    // "All legal entities" must not send a header/account legalEntityId — only an
    // explicitly picked branch (or a single ticked entity) scopes the list.
    if (allLegalEntities) {
      return contactPropertyListScope(null, scopeBranchIdForQueries);
    }
    return contactPropertyListScope(scopeLegalEntityId, scopeBranchIdForQueries);
  }, [allLegalEntities, scopeLegalEntityId, scopeBranchIdForQueries]);

  /**
   * Drop a picked property when the ground it was picked on moves. Done at the two call
   * sites rather than in an effect on the scope: an effect would also fire while the
   * drawer is opening, wiping the property an edit had just restored.
   */
  const clearProperty = useCallback(() => {
    setPropertySearch("");
    set("propertyId", "");
  }, [set]);

  /**
   * The scoped picks a branch change would throw away, named for the confirmation
   * prompt. The property is left out: it only exists once a template is chosen, so
   * it is always covered by the template being listed.
   */
  const scopeSelectionsAtRisk = useMemo(() => {
    const at: string[] = [];
    if (form.templateId.trim()) at.push("template");
    const hasManualLabel = form.manualLabels.some((l) => l.id.trim() || l.name.trim());
    if (form.labelIds.length > 0 || hasManualLabel) at.push("labels");
    return at;
  }, [form.templateId, form.labelIds, form.manualLabels]);

  /**
   * Move the drawer to another branch, dropping everything scoped to the previous
   * one — the template, labels and property are all looked up per entity/branch, so
   * keeping them would leave the form pointing at records the new branch lacks.
   */
  const applyScopeChange = useCallback(
    (nextEntityIds: string[], nextBranchId: string) => {
      setPickerEntityIds(nextEntityIds);
      setPickerBranchId(nextBranchId);
      setForm((prev) => ({
        ...prev,
        templateId: "",
        labelIds: [],
        manualLabels: [{ id: "", name: "" }],
      }));
      clearProperty();
      // The CSV imports its contacts into the branch it was uploaded under, so it
      // goes with everything else scoped to the branch being left.
      setCsvFileName("");
      setCsv(null);
      setPendingScope(null);
    },
    [clearProperty],
  );

  const fetchProperties = useCallback(
    (search: string, page: number, append: boolean) => {
      const ticket = ++propertyFetchSeq.current;
      setLoadingProperties(true);
      propertiesService
        .list(
          {
            ...propertyScope,
            ...(search.trim() ? { search: search.trim() } : {}),
            // Stops the axios interceptor filling legalEntityId from the header
            // account when the picker is "All legal entities".
            skipGlobalContext: true,
          },
          { page, size: PROPERTY_PAGE_SIZE },
        )
        .then((res) => {
          if (ticket !== propertyFetchSeq.current) return;
          const content = res.content ?? [];
          const hasNext =
            content.length >= PROPERTY_PAGE_SIZE ||
            (typeof res.hasNext === "boolean" ? res.hasNext : (res.page ?? page) + 1 < (res.totalPages ?? 0));
          setProperties((prev) => (append ? [...prev, ...content] : content));
          setPropertyPage(res.page ?? page);
          setPropertiesHasNext(hasNext);

          // After a template switch the previous pick is held until this list can settle
          // it: kept if the new list offers it, dropped once the list has run out of pages
          // without it. While pages remain it stays, since absence from a page is not
          // absence from the list.
          if (revalidatePropertyRef.current) {
            const picked = propertyIdRef.current.trim();
            if (!picked || content.some((p) => p.id === picked)) {
              revalidatePropertyRef.current = false;
            } else if (!hasNext) {
              revalidatePropertyRef.current = false;
              clearProperty();
            }
          }
        })
        // Best-effort, like the template and label lookups: the field reads as empty
        // rather than taking the whole form down with a toast.
        .catch(() => {
          if (ticket !== propertyFetchSeq.current) return;
          if (!append) setProperties([]);
          setPropertiesHasNext(false);
        })
        .finally(() => {
          if (ticket !== propertyFetchSeq.current) return;
          setLoadingProperties(false);
          // A first page has now been seen, whichever way it went — later searches and
          // pages are in-field loading, not something to hold the whole form for.
          if (!append) setPropertiesInitialPending(false);
        });
    },
    [propertyScope, clearProperty],
  );

  useEffect(() => {
    if (!open || !selectedTemplateId) {
      setProperties([]);
      setPropertiesHasNext(false);
      setPropertyPage(0);
      setPropertiesInitialPending(false);
      return;
    }
    fetchProperties(propertySearch, 0, false);
  }, [open, selectedTemplateId, propertySearch, fetchProperties]);

  /**
   * True while the lookups the form is built from are still in flight. The fields are
   * held disabled behind an overlay until they settle: an edit would otherwise render
   * for a beat with raw ids in the template, label and property fields, which reads as
   * data loss rather than as loading.
   */
  const formLoading = open && (loadingLabels || loadingTemplates || propertiesInitialPending);

  const handlePropertyEndReached = useCallback(() => {
    if (loadingProperties || !propertiesHasNext) return;
    fetchProperties(propertySearch, propertyPage + 1, true);
  }, [loadingProperties, propertiesHasNext, fetchProperties, propertySearch, propertyPage]);

  const propertyOptions = useMemo(() => {
    const options = properties.map((p) => ({
      value: p.id,
      label: p.displayAddress || `Property ${p.id.slice(0, 8)}`,
      subtitle: [p.market, p.availabilityStatus].filter(Boolean).join(" · ") || undefined,
    }));
    // As with templates: a campaign being edited can hold a property the current scope or
    // search doesn't return. Keep it in the list so the field shows what the campaign
    // actually holds rather than reading empty and quietly detaching it on save.
    const current = form.propertyId.trim();
    if (current && !options.some((o) => o.value === current)) {
      options.unshift({
        value: current,
        label: `Property ${current.slice(0, 8)}`,
        subtitle: "Not in the current scope",
      });
    }
    return options;
  }, [properties, form.propertyId]);

  const templateOptions = useMemo(() => {
    const options = (templates ?? []).map((t) => ({
      value: String(t.id),
      label: t.templateName || `Template ${t.id}`,
      subtitle: [t.language, t.waStatus].filter(Boolean).join(" · ") || undefined,
    }));
    // A campaign can point at a template the current scope doesn't return —
    // one from another branch, or since deleted. Keep it in the list so the
    // dropdown shows what the campaign actually holds instead of reading empty,
    // which would look like "no template" and quietly drop it on save.
    const current = form.templateId.trim();
    if (current && !options.some((o) => o.value === current)) {
      options.unshift({
        value: current,
        label: `Template ${current}`,
        subtitle: "Not in the current scope",
      });
    }
    return options;
  }, [templates, form.templateId]);

  const labelOptions = useMemo(
    () => (labels ?? []).map((label) => ({ value: String(label.id), label: label.name })),
    [labels],
  );

  const isRecurring = form.sendMode === "RECURRING";
  const isScheduledOnce = form.sendMode === "SCHEDULED_ONCE";

  /** True only when the labels request failed — an empty list still uses the dropdown. */
  const manualLabelEntry = !loadingLabels && labels === null;
  const manualLabels = useMemo(() => usableManualLabels(form.manualLabels), [form.manualLabels]);
  const selectedLabelIds = manualLabelEntry ? manualLabels.map((l) => l.id) : form.labelIds;

  const setManualLabel = (index: number, patch: Partial<{ id: string; name: string }>) => {
    set(
      "manualLabels",
      form.manualLabels.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const addManualLabel = () => set("manualLabels", [...form.manualLabels, { id: "", name: "" }]);

  const removeManualLabel = (index: number) =>
    set(
      "manualLabels",
      form.manualLabels.length === 1 ? [{ id: "", name: "" }] : form.manualLabels.filter((_, i) => i !== index),
    );

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Campaign name is required.";
    if (!form.templateId.trim()) next.templateId = "A template is required.";
    if (!branchChosen) {
      next.branch = "Select a branch before choosing recipients.";
    }
    if (csvMode) {
      // An uploaded list replaces labels entirely — its own column carries them.
      if (!csv) next.csv = "Upload a CSV of recipients.";
      else if (!csv.payload) next.csv = "Fix the problems in the CSV before creating the campaign.";
    } else if (selectedLabelIds.length === 0) {
      // The service rejects a blank label name, so a row needs both halves.
      next.labelIds = manualLabelEntry
        ? "Add at least one label with both an ID and a name."
        : "Select at least one label.";
    }
    if (isScheduledOnce && !form.scheduledAt) next.scheduledAt = "Pick when this campaign should run.";
    if (isRecurring && !form.cadenceCron.trim()) {
      next.cadenceCron = "Choose how often this campaign should run.";
    }
    if (isRecurring && !form.repeatIndefinite && !form.endAt) {
      next.endAt = "Pick an end date, or let the campaign repeat indefinitely.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  /**
   * Label objects for the wire. The service requires a non-blank name on every
   * label, so hand-entered rows carry their own and picked ones are named from
   * the picker list — falling back to whatever the campaign already carried, so
   * an edit doesn't drop the name of a label the list didn't return.
   */
  function buildLabels(): { id: string; name: string }[] {
    if (manualLabelEntry) return manualLabels;
    const nameById = new Map<string, string>(
      [...(campaign?.labels ?? []), ...(labels ?? [])].map((l) => [String(l.id), l.name]),
    );
    return selectedLabelIds.map((id) => ({ id, name: nameById.get(id) ?? "" }));
  }

  /**
   * Create body. Only the fields the chosen send mode actually uses are sent —
   * `status` and `start_at` are server-owned, so the form never states them.
   * Recurring always sends `CUSTOM` plus a cron built from the schedule picker.
   * Times go out as zone-less wall-clock, which the service reads in `timezone`.
   */
  function buildCreateBody(): CampaignWriteBody {
    const body: CampaignWriteBody = {
      name: form.name.trim(),
      // The scope the drawer was filled in under, so the campaign keeps it
      // rather than being re-resolved against whatever the header holds at run
      // time. Branch is omitted when the picker is left entity-wide.
      legal_entity_id: scopeLegalEntityId || null,
      ...(scopeBranchIdForQueries ? { branch_id: scopeBranchIdForQueries } : {}),
      template_id: form.templateId.trim(),
      property_id: form.propertyId.trim() || null,
      target_type: form.targetType,
      send_mode: form.sendMode,
      timezone: form.timezone || null,
      // Mutually exclusive: an uploaded list supplies its own labels column, so
      // sending both would leave the service with two sources of recipients.
      ...(csvMode ? { csv: csv?.payload ?? undefined } : { labels: buildLabels() }),
    };

    if (isScheduledOnce) {
      body.scheduled_at = datetimeLocalToWallClock(form.scheduledAt);
    }

    if (isRecurring) {
      body.cadence = "CUSTOM";
      body.cadence_cron = form.cadenceCron.trim();
      body.repeat_indefinite = form.repeatIndefinite;
      if (!form.repeatIndefinite) body.ends_at = zonedWallClockToUtcIso(form.endAt, form.timezone || null);
    }

    return body;
  }

  /**
   * Patch body — only the fields the user actually touched. As with create,
   * times go out as zone-less wall-clock ("2026-08-20T10:00:00") which the
   * service reads in `timezone`; the "did it change?" check still compares
   * instants so a pure zone switch counts as an edit.
   */
  function buildPatchBody(original: Campaign): Partial<CampaignWriteBody> {
    const patch: Partial<CampaignWriteBody> = {};
    const timezone = form.timezone || null;

    if (form.name.trim() !== (original.name ?? "")) patch.name = form.name.trim();

    // Compared on name as well as id: with hand-entered labels, correcting a
    // name is an edit even though the id set is unchanged.
    const nextLabels = buildLabels();
    const key = (l: { id: string; name: string }) => `${l.id}:${l.name}`;
    const originalKeys = (original.labels ?? []).map((l) => key({ id: String(l.id), name: l.name ?? "" }));
    const sameLabels =
      originalKeys.length === nextLabels.length && nextLabels.every((l) => originalKeys.includes(key(l)));
    if (!sameLabels) patch.labels = nextLabels;

    // Compared as strings so a numeric id from an older row doesn't read as a
    // change against the string the picker holds.
    const nextTemplateId = form.templateId.trim();
    if (nextTemplateId !== String(original.template_id ?? "")) patch.template_id = nextTemplateId;

    // Same string comparison as the template. The property is optional, so clearing it
    // patches an explicit null rather than a blank id.
    const nextPropertyId = form.propertyId.trim();
    if (nextPropertyId !== String(original.property_id ?? "")) patch.property_id = nextPropertyId || null;
    if (form.targetType !== original.target_type) patch.target_type = form.targetType;
    if (timezone !== (original.timezone ?? null)) patch.timezone = timezone;

    // A send-mode switch invalidates the previous mode's schedule, so the whole
    // schedule block is restated rather than diffed field by field.
    const modeChanged = form.sendMode !== original.send_mode;
    if (modeChanged) patch.send_mode = form.sendMode;

    if (isScheduledOnce) {
      const nextInstant = zonedWallClockToUtcIso(form.scheduledAt, timezone);
      if (modeChanged || !sameInstant(nextInstant, original.scheduled_at)) {
        patch.scheduled_at = datetimeLocalToWallClock(form.scheduledAt);
      }
    }

    if (isRecurring) {
      const cron = form.cadenceCron.trim();
      if (modeChanged || original.cadence !== "CUSTOM") patch.cadence = "CUSTOM";
      const originalCron =
        (original.cadence_cron ?? "").trim() || cronFromCadencePreset(original.cadence);
      if (modeChanged || cron !== originalCron) patch.cadence_cron = cron;
      const wasIndefinite = original.end_at == null;
      if (modeChanged || form.repeatIndefinite !== wasIndefinite) {
        patch.repeat_indefinite = form.repeatIndefinite;
      }
      if (!form.repeatIndefinite) {
        const nextInstant = zonedWallClockToUtcIso(form.endAt, timezone);
        if (modeChanged || !sameInstant(nextInstant, original.end_at)) {
          patch.ends_at = zonedWallClockToUtcIso(form.endAt, timezone);
        }
      }
    }

    return patch;
  }

  async function handleSubmit() {
    if (!validate()) return;

    if (isEdit && campaign) {
      const patch = buildPatchBody(campaign);
      if (Object.keys(patch).length === 0) {
        toast.info("No changes to save.");
        onClose();
        return;
      }
      setSaving(true);
      try {
        const saved = await campaignsService.update(campaign.id, patch);
        toast.success(saveMessage(saved.name, "updated"));
        onSaved(saved);
        onClose();
      } catch (error) {
        toast.error(getApiErrorMessage(error) ?? "Failed to update the campaign. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!csvMode) {
      setSaving(true);
      try {
        const preview = await labelsService.previewReachout(
          selectedLabelIds,
          form.targetType,
          form.propertyId,
        );
        setReachoutPreview(preview);
      } catch (error) {
        toast.error(
          getApiErrorMessage(error) ??
            "Could not check whether these contacts can be messaged. Please try again.",
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    // A CSV import writes to Atlas contacts, so the rows are scanned against the
    // records already there first: anything that would change an existing contact
    // is put to the user before the import call is made at all.
    const payload = csv?.payload;
    if (!payload) return;

    setSaving(true);
    try {
      const scanned = readScanResponse(
        await campaignsService.scanContacts({
          actor_user_id: actingUserId,
          legal_entity_id: scopeLegalEntityId ?? "",
          // The scan wants the field present even for an entity-wide campaign,
          // where "0" stands in for no branch.
          branch_id: scopeBranchIdForQueries || "0",
          target_type: form.targetType,
          rows: toContactScanRows(payload),
        }),
      );
      if (scanned.failed) {
        toast.error(
          scanned.errorMessage ??
            "Could not check the uploaded contacts against your existing ones. Please try again.",
        );
        return;
      }
      if (scanned.results.length === 0) {
        toast.error("The scan did not return any rows to review. Please check the CSV and try again.");
        return;
      }
      setScanReview({ results: scanned.results });
    } catch (error) {
      toast.error(
        getApiErrorMessage(error) ??
          "Could not check the uploaded contacts against your existing ones. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  /**
   * Create the campaign and report the outcome. Shared by the plain path and by
   * the contact-scan confirmation, which arrives with `body.csv` already narrowed
   * to the rows the user chose to import.
   */
  async function submitCreate(body: CampaignWriteBody, recipients: number) {
    setSaving(true);
    try {
      // A CSV campaign has to import its contacts first, so it goes to the
      // import endpoint rather than the plain create one.
      const saved = body.csv
        ? await campaignsService.createFromCsv(body, scopeLegalEntityId)
        : await campaignsService.create(body);
      toast.success(
        saveMessage(
          saved.name,
          "created",
          body.csv
            ? `${recipients} recipient${recipients === 1 ? "" : "s"} imported. Saved as a draft — activate it when contacts are ready.`
            : recipients > 0
              ? `${recipients} contact${recipients === 1 ? " is" : "s are"} ready to receive. Saved as a draft — activate it when they are ready.`
              : "Saved as a draft — activate it when contacts are ready.",
        ),
      );
      setImportErrors(null);
      setScanReview(null);
      setReachoutPreview(null);
      setPendingCsv(null);
      setImportedLabels([]);
      onSaved(saved);
      onClose();
    } catch (error) {
      // A rejected import comes back with one entry per bad cell — those go to
      // a modal so the user can work through them, and the drawer stays open
      // with the file still attached. Anything else is a plain toast.
      const rowErrors = getCampaignImportRowErrors(error);
      if (rowErrors) {
        setScanReview(null);
        setImportErrors({ errors: rowErrors, detail: getApiErrorMessage(error) });
      } else {
        toast.error(getApiErrorMessage(error) ?? "Failed to create the campaign. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  /**
   * After the CSV scan is accepted: import into Atlas (create missing labels
   * and assign contacts), then dry-run WhatsApp sendability on those label ids.
   * Campaign create uses the imported labels, not a second CSV import.
   */
  async function importThenPreviewCsv(csvPayload: CampaignCsv, scanResults: CampaignContactScanResult[]) {
    if (scanResults.length === 0) {
      toast.error("Review the scan results before importing these contacts.");
      return;
    }

    setSaving(true);
    try {
      const imported = await campaignsService.importContacts({
        actor_user_id: actingUserId,
        legal_entity_id: scopeLegalEntityId ?? "",
        branch_id: scopeBranchIdForQueries || "0",
        target_type: form.targetType,
        rows: toContactScanRows(csvPayload),
      });
      const status = (imported?.status ?? "").toUpperCase();
      const atlasErrors = Array.isArray(imported?.errors) ? imported.errors : [];
      if (status === "FAIL" || status === "FAILED" || status === "ERROR" || atlasErrors.length > 0) {
        setImportErrors({
          errors: atlasErrors,
          detail: "Import failed. Fix the rows below and try again.",
        });
        return;
      }

      const labelsFromImport: { id: string; name: string }[] = (imported.labels ?? [])
        .map((label) => ({
          id: String(label.id ?? "").trim(),
          name: String(label.name ?? "").trim(),
        }))
        .filter((label) => label.id && label.name);
      if (labelsFromImport.length === 0) {
        toast.error("Import did not return any labels, so messaging cannot be checked yet.");
        return;
      }

      const preview = await labelsService.previewReachout(
        labelsFromImport.map((label) => String(label.id)),
        form.targetType,
        form.propertyId,
      );
      setImportedLabels(labelsFromImport);
      setPendingCsv(csvPayload);
      setScanReview(null);
      setReachoutPreview(preview);
    } catch (error) {
      const rowErrors = getCampaignImportRowErrors(error);
      if (rowErrors) {
        setImportErrors({ errors: rowErrors, detail: getApiErrorMessage(error) });
      } else {
        toast.error(
          getApiErrorMessage(error) ??
            "Could not import these contacts. Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  /**
   * Import the rows ticked in the scan review. Unticked rows are dropped from
   * the payload — including new contacts the user chose not to create.
   */
  async function handleScanConfirm(selectedRowNumbers: number[]) {
    const payload = csv?.payload;
    if (!payload || !scanReview) return;

    const selected = new Set(selectedRowNumbers);
    const scannedNumbers = new Set(scanReview.results.map((r) => r.row_number));
    const rows = payload.rows.filter(
      (row) => selected.has(row.row_number) || !scannedNumbers.has(row.row_number),
    );
    if (rows.length === 0) {
      toast.info("Select at least one row to import.");
      return;
    }

    await importThenPreviewCsv({ ...payload, rows }, scanReview.results);
  }

  async function handleReachoutConfirm() {
    if (!reachoutPreview) return;
    if (importedLabels.length > 0) {
      const body = buildCreateBody();
      delete body.csv;
      await submitCreate({ ...body, labels: importedLabels }, reachoutPreview.ready);
      return;
    }
    if (pendingCsv) {
      await submitCreate({ ...buildCreateBody(), csv: pendingCsv }, pendingCsv.rows.length);
      return;
    }
    await submitCreate(buildCreateBody(), reachoutPreview.ready);
  }

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Campaign" : "New Campaign"}
      subtitle={isEdit ? (
        <span className="group/copy inline-flex items-center gap-1">
          <span>ID: {campaign?.id}</span>
          <CopyButton
            value={campaign?.id}
            label="Campaign ID"
            successMessage="Record ID copied to clipboard"
          />
        </span>
      ) : "Set up an audience, template and schedule."}
      width="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving} disabled={formLoading}>
            {isEdit ? "Save Changes" : "Create Campaign"}
          </Button>
        </>
      }
    >
      <div className="relative">
        {/* Held disabled behind an overlay until the scope's templates, labels and
            properties are in: the fields would otherwise show the ids the campaign
            stores instead of the names they resolve to. Pinned near the top rather
            than centred, so it stays in view on a form taller than the drawer. */}
        {formLoading && (
          <div
            className="absolute inset-0 z-10 flex items-start justify-center pt-24 bg-bridge-surface/70 backdrop-blur-[1px]"
            role="status"
          >
            <span className="flex items-center gap-2 text-sm text-bridge-muted">
              <Loader2 size={16} className="animate-spin" />
              Loading campaign…
            </span>
          </div>
        )}
        {/* A fieldset so every control inside is genuinely disabled, not just
            unclickable — nothing can be tabbed into while the lookups are out. */}
        <fieldset
          disabled={formLoading}
          aria-busy={formLoading || undefined}
          className={cn("min-w-0 space-y-5", formLoading && "select-none opacity-40")}
        >
          <FormRow
            label="Legal Entity & Branch"
            hint={
              isEdit
                ? "Fixed once the campaign exists — its template, labels and recipients all belong to this entity and branch."
                : "Sets which templates and labels this campaign can use, and the entity an uploaded recipient list is imported into."
            }
          >
            {/* Locked while editing: the scope decides which templates, labels and
                properties are on offer, so changing it under an existing campaign
                would leave it pointing at records the new scope doesn't contain. */}
            <BranchScopeSelect
              value={isEdit ? campaignBranchId : pickerBranchId}
              entityIds={isEdit ? (campaignLegalEntityId ? [campaignLegalEntityId] : []) : pickerEntityIds}
              disabled={isEdit}
              tooltip={
                isEdit
                  ? "The entity and branch cannot be changed after a campaign is created."
                  : "Sets which templates and labels this campaign can use. All legal entities loads every template."
              }
              placeholder="All legal entities"
              onEntityIdsChange={(ids) => {
                const same =
                  ids.length === pickerEntityIds.length && ids.every((id, i) => id === pickerEntityIds[i]);
                if (same) return;
                if (scopeSelectionsAtRisk.length === 0) {
                  applyScopeChange(ids, pickerBranchId);
                  return;
                }
                setPendingScope({ entityIds: ids, branchId: pickerBranchId });
              }}
              onChange={(id, branch) => {
                const nextEntityIds = branch ? [branch.legalEntityId] : pickerEntityIds;
                if (id === pickerBranchId) return;
                if (scopeSelectionsAtRisk.length === 0) {
                  applyScopeChange(nextEntityIds, id);
                  return;
                }
                setPendingScope({ entityIds: nextEntityIds, branchId: id });
              }}
            />
            {errors.branch && <p className="mt-1 text-xs text-bridge-danger">{errors.branch}</p>}
            {!branchChosen && (
              <p className="mt-1 text-xs font-medium text-red-600">
                Pick a branch to continue — the rest of the form stays locked until then.
              </p>
            )}
          </FormRow>

          {/* Everything below the scope depends on the branch: templates, labels,
              properties and recipients are all listed per branch. Until one is
              picked the rest of the form is inert rather than offering empty lists. */}
          <div
            aria-disabled={!branchChosen || undefined}
            className={cn(
              "min-w-0 space-y-5",
              !branchChosen && "pointer-events-none select-none opacity-40",
            )}
          >
            <FormRow label="Campaign Name" required>
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Property 301 Advertisement"
              />
              {errors.name && <p className="mt-1 text-xs text-bridge-danger">{errors.name}</p>}
            </FormRow>

            <FormRow label="Channel" required>
              <MenuSelect
                hideLabel
                value={form.targetType}
                onChange={(v) => set("targetType", v)}
                options={TARGET_TYPE_FORM_OPTIONS}
              />
            </FormRow>

            <FormRow
              label="Template"
              required
              hint={
                loadingTemplates
                  ? "Loading templates…"
                  : templates != null && templates.length === 0
                    ? "No WhatsApp templates found for this selection."
                    : allLegalEntities
                      ? "Showing templates across all legal entities."
                      : "Templates for the selected legal entity and branch."
              }
            >
              <MenuSelect
                hideLabel
                enableSearch
                value={form.templateId}
                onChange={(v) => {
                  const next = v.trim();
                  if (next === selectedTemplateId) return;
                  set("templateId", v);
                  // Clearing the template disables the property field, so its value goes
                  // with it. Switching to another template rebuilds the list instead, and
                  // the pick is only dropped if that list turns out not to contain it.
                  if (!next) clearProperty();
                  else if (form.propertyId.trim()) revalidatePropertyRef.current = true;
                  setPropertySearch("");
                }}
                options={
                  loadingTemplates
                    ? [{ value: "", label: "Loading templates…" }]
                    : templateOptions.length > 0
                      ? templateOptions
                      : [{ value: "", label: "No templates for this legal entity and branch" }]
                }
                placeholder="Select a template"
                disabled={loadingTemplates || templateOptions.length === 0}
              />
              {errors.templateId && <p className="mt-1 text-xs text-bridge-danger">{errors.templateId}</p>}
            </FormRow>

            <FormRow
              label="Property"
              hint={
                templateChosen
                  ? "Optional — properties come from the legal entity and branch above."
                  : "Select a template first — the property is what fills its placeholders."
              }
            >
              <MenuSelect
                hideLabel
                searchable
                value={form.propertyId}
                onChange={(v) => set("propertyId", v)}
                options={propertyOptions}
                disabled={!templateChosen}
                placeholder={
                  !templateChosen
                    ? "Select a template first"
                    : propertyOptions.length === 0 && !loadingProperties
                      ? "No properties in this scope"
                      : "Select a property"
                }
                onDropdownSearchQueryChange={setPropertySearch}
                onDropdownEndReached={handlePropertyEndReached}
                loading={loadingProperties}
                hasMorePages={propertiesHasNext}
              />
              {errors.propertyId && <p className="mt-1 text-xs text-bridge-danger">{errors.propertyId}</p>}
            </FormRow>

            {!isEdit && (
              <FormRow
                label="Recipients"
                hint={branchChosen ? undefined : "Pick a branch above before choosing who this campaign goes to."}
              >
                <div className="inline-flex rounded-md border border-bridge-border/70 p-0.5">
                  {(
                    [
                      { value: "labels", label: "Labels" },
                      { value: "csv", label: "CSV upload" },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setRecipientSource(option.value)}
                      className={cn(
                        "h-8 rounded px-3 text-xs font-medium transition-colors",
                        recipientSource === option.value
                          ? "bg-bridge-accent text-white"
                          : "text-bridge-muted hover:text-bridge-fg",
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </FormRow>
            )}

            {csvMode ? (
              <FormRow
                label="Recipient CSV"
                required
                hint={
                  branchChosen
                    ? `Line 1 holds the Mandatory flags, line 2 the column headers, and data starts on line 3. Up to ${MAX_CSV_ROWS} rows.`
                    : "Select a branch above first — the uploaded contacts are imported into it."
                }
              >
                <label
                  className={cn(
                    "flex items-center gap-3 rounded-md border border-dashed border-bridge-border px-4 py-3 transition-colors",
                    branchChosen
                      ? "cursor-pointer hover:border-bridge-accent/60"
                      : "cursor-not-allowed opacity-60",
                  )}
                >
                  <Upload size={16} className={cn("shrink-0", branchChosen ? "text-bridge-accent" : "text-bridge-muted")} />
                  <span className="min-w-0 flex-1 truncate text-sm text-bridge-fg">
                    {branchChosen ? csvFileName || "Choose a CSV file…" : "Select a branch first"}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 text-xs font-medium",
                      branchChosen ? "text-bridge-accent" : "text-bridge-muted",
                    )}
                  >
                    Browse
                  </span>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    disabled={!branchChosen}
                    onChange={(e) => {
                      void handleCsvFile(e.target.files?.[0]);
                      // Cleared so re-picking the same filename fires onChange again.
                      e.target.value = "";
                    }}
                  />
                </label>

                <a
                  href={SAMPLE_CSV_URL}
                  download={SAMPLE_CSV_FILENAME}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-bridge-accent hover:underline"
                >
                  <Download size={13} className="shrink-0" />
                  Download sample CSV
                </a>

                {csv && csv.errors.length > 0 && (
                  <ul className="mt-2 space-y-1 rounded-md border border-bridge-danger/20 bg-bridge-danger/10 px-3 py-2">
                    {csv.errors.map((problem, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-bridge-danger">
                        <AlertTriangle size={13} className="mt-px shrink-0" />
                        <span>{problem}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {csv?.payload && (
                  <div className="mt-2 rounded-md border border-bridge-border/70 bg-bridge-bg px-3 py-2.5">
                    <div className="flex items-center gap-2 text-sm font-medium text-bridge-fg">
                      <Check size={14} className="text-bridge-success" />
                      {csv.rowCount} recipient{csv.rowCount === 1 ? "" : "s"} · {csv.headers.length} columns
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {csv.headers.map((header) => (
                        <Badge key={header} tone={csv.mandatoryHeaders.includes(header) ? "accent" : "neutral"}>
                          {header}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {errors.csv && <p className="mt-1 text-xs text-bridge-danger">{errors.csv}</p>}
              </FormRow>
            ) : (
            <FormRow
              label="Labels"
              required
              hint={
                !branchChosen
                  ? "Select a branch above first — labels are listed per branch."
                  : manualLabelEntry
                    ? "Label list unavailable — enter each label's ID and name as they exist in the campaign service."
                    : undefined
              }
            >
              {!manualLabelEntry ? (
                <MultiSelect
                  hideLabel
                  enableSearch
                  value={form.labelIds}
                  onChange={(next) => set("labelIds", next)}
                  options={labelOptions}
                  disabled={!branchChosen}
                  placeholder={branchChosen ? "Select labels" : "Select a branch first"}
                  searchPlaceholder="Search labels…"
                  emptyText="No labels for this scope"
                  loading={loadingLabels}
                />
              ) : (
                <div className="space-y-2">
                  {form.manualLabels.map((row, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        className="w-[170px] shrink-0"
                        value={row.id}
                        disabled={!branchChosen}
                        onChange={(e) => setManualLabel(index, { id: e.target.value })}
                        placeholder="Label ID"
                        aria-label={`Label ${index + 1} ID`}
                      />
                      <Input
                        className="flex-1"
                        value={row.name}
                        disabled={!branchChosen}
                        onChange={(e) => setManualLabel(index, { name: e.target.value })}
                        placeholder="Label name, e.g. VIP Customers"
                        aria-label={`Label ${index + 1} name`}
                      />
                      <IconButton label={`Remove label ${index + 1}`} onClick={() => removeManualLabel(index)}>
                        <Trash2 size={15} />
                      </IconButton>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addManualLabel}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-bridge-accent hover:underline"
                  >
                    <Plus size={14} />
                    Add another label
                  </button>
                </div>
              )}
              {errors.labelIds && <p className="mt-1 text-xs text-bridge-danger">{errors.labelIds}</p>}
            </FormRow>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormRow label="Send Mode" required>
                <MenuSelect
                  hideLabel
                  value={form.sendMode}
                  onChange={(v) => set("sendMode", v)}
                  options={SEND_MODE_OPTIONS}
                />
              </FormRow>
              <FormRow label="Timezone">
                <MenuSelect
                  hideLabel
                  value={form.timezone}
                  onChange={(v) => set("timezone", v)}
                  options={TIMEZONE_OPTIONS}
                  enableSearch
                  placeholder="Select timezone"
                />
              </FormRow>
            </div>

            {isScheduledOnce && (
              <FormRow label="Scheduled For" required hint={`Interpreted in ${form.timezone || DEFAULT_TIMEZONE}.`}>
                <DiaryDateTimeField
                  id="campaign-scheduled-at"
                  label=""
                  value={form.scheduledAt}
                  onChange={(v) => set("scheduledAt", v)}
                />
                {errors.scheduledAt && <p className="mt-1 text-xs text-bridge-danger">{errors.scheduledAt}</p>}
              </FormRow>
            )}

            {isRecurring && (
              <div className="space-y-5">
                <RecurringScheduleFields
                  cron={form.cadenceCron}
                  onChange={(cron) => {
                    set("cadence", "CUSTOM");
                    set("cadenceCron", cron);
                  }}
                  error={errors.cadenceCron}
                />

                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={form.repeatIndefinite}
                    onChange={(e) => set("repeatIndefinite", e.target.checked)}
                    className="h-4 w-4 accent-bridge-accent"
                  />
                  <span className="text-sm text-bridge-fg">Repeat indefinitely</span>
                </label>

                {!form.repeatIndefinite && (
                  <FormRow label="Ends" required hint={`Interpreted in ${form.timezone || DEFAULT_TIMEZONE}.`}>
                    <DiaryDateTimeField
                      id="campaign-end-at"
                      label=""
                      value={form.endAt}
                      onChange={(v) => set("endAt", v)}
                    />
                    {errors.endAt && <p className="mt-1 text-xs text-bridge-danger">{errors.endAt}</p>}
                  </FormRow>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </div>
    </Drawer>

    <CampaignContactScanModal
      open={scanReview !== null && reachoutPreview === null}
      results={scanReview?.results ?? []}
      submitting={saving}
      onCancel={() => setScanReview(null)}
      onConfirm={(rows) => void handleScanConfirm(rows)}
    />

    <CampaignReachoutPreviewModal
      open={reachoutPreview !== null}
      preview={reachoutPreview}
      submitting={saving}
      onCancel={() => {
        setReachoutPreview(null);
        setPendingCsv(null);
        setImportedLabels([]);
      }}
      onConfirm={() => void handleReachoutConfirm()}
    />

    <CampaignScopeChangeModal
      open={pendingScope !== null}
      affected={scopeSelectionsAtRisk}
      onCancel={() => setPendingScope(null)}
      onConfirm={() =>
        applyScopeChange(pendingScope?.entityIds ?? [], pendingScope?.branchId ?? "")
      }
    />

    <CampaignImportErrorsModal
      open={importErrors != null}
      detail={importErrors?.detail}
      errors={importErrors?.errors ?? []}
      onClose={() => setImportErrors(null)}
    />
    </>
  );
}
